import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Project } from '../types';
import ScrollToTop from '../components/ScrollToTop';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 添加状态用于显示弹出图片
  const [modalImage, setModalImage] = useState<string | null>(null);
  // 添加当前查看的图片索引和所有图片数组状态
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [allProjectImages, setAllProjectImages] = useState<string[]>([]);
  // 添加相关项目状态
  const [relatedProjects, setRelatedProjects] = useState<Project[]>([]);
  // 添加前后项目导航
  const [prevProject, setPrevProject] = useState<Project | null>(null);
  const [nextProject, setNextProject] = useState<Project | null>(null);
  // 添加鼠标悬停状态
  const [showPrevPreview, setShowPrevPreview] = useState(false);
  const [showNextPreview, setShowNextPreview] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      if (!id || isNaN(Number(id))) {
        setError(t('projects.notFound'));
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/projects/${id}`);
        setProject(response.data);
        
        // 获取所有项目以找出推荐项目和导航项目
        const allProjectsResponse = await api.get('/projects');
        const allProjects = allProjectsResponse.data.filter((p: Project) => 
          p.image_url && p.image_url.trim() !== ''
        );
        
        if (allProjects && allProjects.length > 0) {
          // 根据ID排序所有项目
          const sortedProjects = [...allProjects].sort((a, b) => a.id - b.id);
          
          // 找到当前项目在排序列表中的索引
          const currentIndex = sortedProjects.findIndex(p => p.id === Number(id));
          
          if (currentIndex !== -1) {
            const totalProjects = sortedProjects.length;
            
            // 设置前一个项目（如果当前是第一个，则取最后一个）
            const prevIndex = (currentIndex - 1 + totalProjects) % totalProjects;
            setPrevProject(sortedProjects[prevIndex]);
            
            // 设置后一个项目（如果当前是最后一个，则取第一个）
            const nextIndex = (currentIndex + 1) % totalProjects;
            setNextProject(sortedProjects[nextIndex]);
            
            // 同分类推荐项目处理
            if (response.data.category_ids?.length > 0) {
              const currentCategoryId = response.data.category_ids[0];
              
              // 筛选出同一分类的其他项目（排除当前项目）
              const sameCategoryProjects = allProjects.filter((p: Project) => 
                p.id !== Number(id) && 
                p.category_ids.includes(currentCategoryId)
              );
              
              // 从同类项目中随机选取4个项目（如果不足4个则全部选取）
              let recommendedProjects = [];
              if (sameCategoryProjects.length <= 4) {
                recommendedProjects = sameCategoryProjects;
              } else {
                // 随机打乱数组并选取前4个
                recommendedProjects = [...sameCategoryProjects]
                  .sort(() => Math.random() - 0.5)
                  .slice(0, 4);
              }
              
              setRelatedProjects(recommendedProjects);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching project:', error);
        setError(t('projects.notFound'));
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id, t]);

  // 导航到前一个项目
  const goToPrevProject = () => {
    if (prevProject) {
      // 强制页面重新加载以确保内容更新
      window.location.href = `/projects/${prevProject.id}`;
    }
  };

  // 导航到后一个项目
  const goToNextProject = () => {
    if (nextProject) {
      // 强制页面重新加载以确保内容更新
      window.location.href = `/projects/${nextProject.id}`;
    }
  };

  // 解析中英文格式的键值（例如"中文名|English Name"）
  const getLocalizedKey = (key: string): string => {
    // 如果key包含'|'分隔符，按照当前语言选择相应部分
    if (key.includes('|')) {
      const [zhKey, enKey] = key.split('|');
      return i18n.language === 'zh' ? zhKey : (enKey || zhKey);
    }
    
    // 检查 properties 对象中是否有对应的 key_en 属性
    if (project && project.properties && key in project.properties) {
      const property = project.properties[key];
      if (property && typeof property === 'object' && 'key_en' in property) {
        return i18n.language === 'zh' ? key : (property.key_en || key);
      }
    }
    
    return key;
  };

  // 处理图片点击事件
  const handleImageClick = (url: string) => {
    // 找出所有项目图片URLs并展平为一个数组
    const allImages: string[] = [];
    project?.images?.forEach(group => {
      group.image_urls.forEach(imgUrl => {
        allImages.push(imgUrl);
      });
    });
    
    setAllProjectImages(allImages);
    const index = allImages.findIndex(img => img === url);
    setCurrentImageIndex(index !== -1 ? index : 0);
    setModalImage(url);
  };

  // 关闭图片弹窗
  const handleCloseModal = () => {
    setModalImage(null);
    setCurrentImageIndex(0);
  };

  // 切换到上一张图片
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allProjectImages.length <= 1) return;
    
    const newIndex = (currentImageIndex - 1 + allProjectImages.length) % allProjectImages.length;
    setCurrentImageIndex(newIndex);
    setModalImage(allProjectImages[newIndex]);
  };

  // 切换到下一张图片
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allProjectImages.length <= 1) return;
    
    const newIndex = (currentImageIndex + 1) % allProjectImages.length;
    setCurrentImageIndex(newIndex);
    setModalImage(allProjectImages[newIndex]);
  };
  
  useEffect(() => {
    if (!modalImage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        if (allProjectImages.length > 1) {
          setCurrentImageIndex(prev => {
            const newIndex = (prev - 1 + allProjectImages.length) % allProjectImages.length;
            setModalImage(allProjectImages[newIndex]);
            return newIndex;
          });
        }
      } else if (e.key === 'ArrowRight') {
        if (allProjectImages.length > 1) {
          setCurrentImageIndex(prev => {
            const newIndex = (prev + 1) % allProjectImages.length;
            setModalImage(allProjectImages[newIndex]);
            return newIndex;
          });
        }
      } else if (e.key === 'Escape') {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalImage, allProjectImages, handleCloseModal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900 mb-4"></div>
            <p className="text-gray-600 font-medium">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-red-500">{error || t('projects.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[80px] min-h-screen bg-gray-50 font-PingFang">
      <ScrollToTop />
      
      {/* 项目导航组件 - 常驻屏幕两侧 */}
      {prevProject && (
        <div 
          className="fixed left-0 top-1/2 transform -translate-y-1/2 z-40"
          onMouseEnter={() => setShowPrevPreview(true)}
          onMouseLeave={() => setShowPrevPreview(false)}
          onClick={goToPrevProject}
        >
          <div className="bg-black bg-opacity-30 hover:bg-opacity-50 text-white p-4 rounded-r-lg transition-all cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
          
          {/* 预览卡片 */}
          {showPrevPreview && (
            <div className="absolute top-0 left-12 bg-white shadow-xl rounded-lg overflow-hidden transition-opacity duration-300 w-48 cursor-pointer">
              {prevProject.image_url && (
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src={prevProject.image_url} 
                    alt={i18n.language === 'zh' ? prevProject.title : prevProject.title_en}
                    className="w-full h-full object-cover" 
                  />
                </div>
              )}
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {i18n.language === 'zh' ? prevProject.title : prevProject.title_en}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {i18n.language === 'zh' ? '上一个项目' : 'Previous Project'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {nextProject && (
        <div 
          className="fixed right-0 top-1/2 transform -translate-y-1/2 z-40"
          onMouseEnter={() => setShowNextPreview(true)}
          onMouseLeave={() => setShowNextPreview(false)}
          onClick={goToNextProject}
        >
          <div className="bg-black bg-opacity-30 hover:bg-opacity-50 text-white p-4 rounded-l-lg transition-all cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          
          {/* 预览卡片 */}
          {showNextPreview && (
            <div className="absolute top-0 right-12 bg-white shadow-xl rounded-lg overflow-hidden transition-opacity duration-300 w-48 cursor-pointer">
              {nextProject.image_url && (
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src={nextProject.image_url} 
                    alt={i18n.language === 'zh' ? nextProject.title : nextProject.title_en}
                    className="w-full h-full object-cover" 
                  />
                </div>
              )}
              <div className="p-3">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {i18n.language === 'zh' ? nextProject.title : nextProject.title_en}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {i18n.language === 'zh' ? '下一个项目' : 'Next Project'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Banner Section */}
      <div className="relative h-[250px] md:h-[886px]">
        <img
          src={project.image_url}
          alt={i18n.language === 'zh' ? project.title : project.title_en}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto px-4 md:px-12 max-w-[2000px]">
          {/* 项目路径导航 */}
          <div className="mb-6">
            <div className="flex items-center text-sm">
              <Link to="/" className="text-gray-500 hover:text-gray-700">
                {i18n.language === 'zh' ? '首页' : 'Home'}
              </Link>
              <span className="mx-2 text-gray-400">/</span>
              <Link to="/projects" className="text-gray-500 hover:text-gray-700">
                {i18n.language === 'zh' ? '项目' : 'Projects'}
              </Link>
              {project.category_ids && project.category_ids.length > 0 && (
                <>
                  <span className="mx-2 text-gray-400">/</span>
                  <div className="flex flex-wrap items-center">
                    {project.category_ids.map((categoryId, index) => {
                      // 找到对应的分类名称
                      const categoryNames = Array.isArray(project.category_names) 
                        ? project.category_names 
                        : project.category_names.split(',').map(name => name.trim());
                      
                      const categoryNamesEn = Array.isArray(project.category_names_en)
                        ? project.category_names_en
                        : (project.category_names_en ? project.category_names_en.split(',').map(name => name.trim()) : []);
                      
                      const categoryName = i18n.language === 'zh' 
                        ? categoryNames[index] 
                        : (categoryNamesEn[index] || categoryNames[index]);
                      
                      return (
                        <React.Fragment key={categoryId}>
                          {index > 0 && <span className="mx-1 text-gray-400">,</span>}
                          <Link 
                            to={`/projects?category=${categoryId}`} 
                            className="text-gray-600 hover:text-black hover:underline"
                          >
                            {categoryName}
                          </Link>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </>
              )}
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-gray-900 font-medium">
                {i18n.language === 'zh' ? project.title : project.title_en}
              </span>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">
              {i18n.language === 'zh' ? project.title : project.title_en}
            </h1>
            <p className="text-xl text-gray-600">
              {i18n.language === 'zh' ? project.description : project.description_en}
            </p>
          </div>
          <div className="w-full h-[1px] bg-gray-200 mb-12"></div>

          {/* Properties Section */}
          <div className="mb-12">
            <div className="bg-gray-50 p-8 rounded-lg">
              {/* Categories */}
              <div className="mb-12">
                <h3 className="text-sm mb-3">{getLocalizedKey('分类|Program')}</h3>
                <div className="flex flex-wrap gap-4">
                  {(Array.isArray(project.category_names) ? project.category_names : project.category_names.split(',')).map((name, index) => (
                    <span key={index} className="text-base text-gray-900">
                      {i18n.language === 'zh' 
                        ? name.trim()
                        : Array.isArray(project.category_names_en) 
                          ? project.category_names_en[index]?.trim() 
                          : project.category_names_en?.split(',')[index]?.trim()}
                    </span>
                  ))}
                </div>
              </div>

              {/* Other Properties */}
              {project.properties && Object.entries(project.properties).length > 0 && (
                <div className="space-y-4">
                  {Object.entries(project.properties).map(([key, value], index) => {
                    // 获取本地化显示的key
                    // 检查当前属性是否有对应的 key_en
                    let localizedKey;
                    if (typeof value === 'object' && 'key_en' in value) {
                      localizedKey = i18n.language === 'zh' ? key : (value.key_en || key);
                    } else {
                      localizedKey = getLocalizedKey(key);
                    }
                    
                    // 如果value是数组，处理新的属性格式（数组格式）
                    if (Array.isArray(value) && value.length > 0) {
                      // 找到数组中与key匹配的项
                      const prop = value.find(p => p.key === key);
                      if (prop) {
                        // 处理属性值的本地化显示
                        const localizedValue = i18n.language === 'zh' 
                          ? prop.value 
                          : (prop.value_en || prop.value);
                        
                        return (
                          <div key={index} className={`flex ${i18n.language === 'zh' ? 'items-start' : 'flex-col'}`}>
                            <span className={`text-base text-gray-600 ${i18n.language === 'zh' ? 'w-24' : 'mb-1'}`}>
                              {localizedKey}
                            </span>
                            <span className="text-base text-gray-900 flex-1">
                              {localizedValue}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    }
                    
                    // 处理字符串格式的属性
                    const propertyValue = typeof value === 'object' ? value : { value: value, value_en: value };
                    
                    // 处理属性值的本地化显示
                    const localizedValue = i18n.language === 'zh'
                      ? propertyValue.value
                      : (propertyValue.value_en || propertyValue.value);
                    
                    return (
                      <div key={index} className={`flex ${i18n.language === 'zh' ? 'items-start' : 'flex-col'}`}>
                        <span className={`text-base text-gray-600 ${i18n.language === 'zh' ? 'w-24' : 'mb-1'}`}>
                          {localizedKey}
                        </span>
                        <span className="text-base text-gray-900 flex-1">
                          {localizedValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-gray-200 mb-12"></div>

          {/* Description Section */}
          <div className="prose prose-lg max-w-none">
            <div 
              dangerouslySetInnerHTML={{ 
                __html: (i18n.language === 'zh' ? project.content : project.content_en)
                  ?.replace(/\n/g, '<br />') || '' 
              }} 
            />
          </div>

          {/* Project Images Section */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">
              {getLocalizedKey('项目图片|Project Images')}
            </h2>
            <div className="space-y-8">
              {project.images?.map((imageGroup, index) => (
                <div key={index} className={`grid gap-4 ${
                  imageGroup.layout_type === 'one_horizontal' 
                    ? 'grid-cols-1' 
                    : 'grid-cols-1 md:grid-cols-2'
                }`}>
                  {imageGroup.image_urls.map((url, imgIndex) => (
                    <div 
                      key={imgIndex} 
                      className="relative group cursor-pointer overflow-hidden"
                      onClick={() => handleImageClick(url)}
                    >
                      <img
                        src={url}
                        alt={`Project image ${index + 1}-${imgIndex + 1}`}
                        className={`w-full ${
                          imageGroup.layout_type === 'two_vertical' ? 'aspect-[3/4]' : 
                          imageGroup.layout_type === 'one_horizontal' ? 'aspect-[16/9]' : 
                          'aspect-[4/3]'
                        } object-cover pointer-events-none transition-transform duration-300 group-hover:scale-105`}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* 推荐项目部分 */}
          {relatedProjects.length > 0 && (
            <div className="mt-20">
              <div className="relative mb-12">
                <h2 className="text-2xl font-bold mb-6">
                  {i18n.language === 'zh' ? '推荐项目' : 'Recommended Projects'}
                </h2>
                <div className="absolute -bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
                {relatedProjects.map((relatedProject) => (
                  <Link
                    key={relatedProject.id}
                    to={`/projects/${relatedProject.id}`}
                    className="group block bg-white"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={relatedProject.image_url}
                        alt={i18n.language === 'zh' ? relatedProject.title : relatedProject.title_en}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="mt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {i18n.language === 'zh' ? relatedProject.title : relatedProject.title_en}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {i18n.language === 'zh' ? relatedProject.description : relatedProject.description_en}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4" 
          onClick={handleCloseModal}
        >
          <div className="relative w-full md:w-3/4 max-h-[90vh] flex items-center justify-center">
            {/* 上一张按钮 */}
            {allProjectImages.length > 1 && (
              <button 
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all z-10 focus:outline-none"
                aria-label={i18n.language === 'zh' ? '上一张' : 'Previous'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            <img 
              src={modalImage} 
              alt="Preview" 
              className="w-auto max-w-full max-h-[85vh] mx-auto object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* 下一张按钮 */}
            {allProjectImages.length > 1 && (
              <button 
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all z-10 focus:outline-none"
                aria-label={i18n.language === 'zh' ? '下一张' : 'Next'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            
            {/* 图片信息 */}
            <div className="absolute bottom-4 left-0 right-0 text-center text-white text-sm">
              {allProjectImages.length > 1 && (
                <span>{currentImageIndex + 1} / {allProjectImages.length}</span>
              )}
            </div>
            
            <button 
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-200"
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail; 
